<template>
  <v-card :disabled="loading" flat>
    <v-card-title class="display-1 justify-center mb-4 mt-n3">
      {{ isEditMode ? "Edit storage" : "Add storage" }}
    </v-card-title>
    <v-card-text class="pa-0">
      <!-- Edit Mode: Storage Info Header -->
      <div v-if="isEditMode" class="storage-info-header mb-4 pa-4 rounded">
        <div class="d-flex align-center">
          <img
            v-if="actionsData.icon"
            :src="getStorageImg(actionsData.icon)"
            height="48"
            width="48"
            class="mr-4"
          />
          <div>
            <div class="font-weight-bold text-h6">{{ actionsData.name }}</div>
            <div class="text-caption grey--text">{{ actionsData.bucket }}</div>
          </div>
        </div>
      </div>

      <!-- Edit Mode: Region & Endpoint (Read-only) -->
      <template v-if="isEditMode">
        <v-text-field
          :value="actionsData.region"
          class="mb-4"
          disabled
          filled
          label="Region"
        />
        <v-text-field
          :value="actionsData.endpoint"
          class="mb-4"
          disabled
          filled
          label="Endpoint"
        />

        <!-- Mode Tabs -->
        <div class="mb-4">
          <div class="text-subtitle-2 mb-2">What would you like to edit?</div>
          <v-btn-toggle v-model="editMode" mandatory class="full-width-toggle">
            <v-btn value="status" class="flex-grow-1 text-transform-none">
              Status
            </v-btn>
            <v-btn value="credentials" class="flex-grow-1 text-transform-none">
              Credentials
            </v-btn>
          </v-btn-toggle>
        </div>

        <!-- Status Mode -->
        <div v-if="editMode === 'status'" class="mb-4">
          <div class="text-subtitle-2 mb-2">Status</div>
          <v-radio-group v-model="formValues.status" row class="mt-0">
            <v-radio label="Active" :value="1" />
            <v-radio label="Inactive" :value="0" />
          </v-radio-group>
          <div v-if="formValues.status === 0" class="text-caption grey--text">
            Inactive storage cannot be used for new apps or releases
          </div>
        </div>

        <!-- Credentials Mode -->
        <template v-if="editMode === 'credentials'">
          <v-alert
            type="warning"
            dense
            text
            class="mb-4"
          >
            <strong>Important:</strong> Before saving, we'll validate that the new credentials have access to the bucket "{{ actionsData.bucket }}". Make sure the credentials have read/write permissions.
          </v-alert>

          <v-text-field
            v-model="formValues.key"
            :error-messages="keyError"
            class="mb-4"
            filled
            label="Access Key"
            placeholder="Enter new access key"
          />
          <v-text-field
            v-model="formValues.secret"
            :error-messages="secretError"
            class="mb-4"
            filled
            label="Secret Key"
            placeholder="Enter new secret key"
            type="password"
          />
        </template>
      </template>

      <!-- Create Mode -->
      <template v-else>
        <v-select
          v-model="formValues.s3_id"
          v-validate="'required'"
          :item-disabled="disableItem"
          :items="providerList"
          :menu-props="{ offsetY: true }"
          append-icon="mdi-chevron-down"
          attach
          class="mb-4"
          filled
          item-text="name"
          item-value="id"
          placeholder="Select Providers"
          v-bind="veeValidate('Providers', 'Providers')"
          @focusin.prevent="getProviderList"
        >
          <template #item="{ item }">
            <div class="d-flex align-center fill-width">
              <v-card
                :disabled="item.status !== 1"
                class="transparent d-flex justify-center align-center my-2 mr-4"
                flat
                style="width: 32px"
                tile
              >
                <img
                  v-if="item.icon"
                  :src="item.icon ? getStorageImg(item.icon) : null"
                  height="32"
                />
              </v-card>
              {{ item.name }}
              <v-spacer />
              <v-chip
                v-if="item.status === 2"
                class="radius-10 bold-text px-3"
                style="height: 26px; padding-bottom: 2px"
              >
                In beta
              </v-chip>
              <v-chip
                v-if="item.status === 3"
                class="radius-10 bold-text px-3"
                style="height: 26px; padding-bottom: 2px"
              >
                Soon
              </v-chip>
              <v-chip
                v-if="item.status === 4"
                class="radius-10 bold-text px-3"
                style="height: 26px; padding-bottom: 2px"
              >
                Future
              </v-chip>
            </div>
          </template>
          <template #no-data>
            <div class="pa-4">
              <div
                v-if="storageLoading"
                class="d-flex align-center justify-center"
              >
                <ContentLoader size="25" />
              </div>
              <div v-else>No data available</div>
            </div>
          </template>
        </v-select>
        <v-text-field
          v-model="formValues.key"
          v-validate="'required'"
          :disabled="!formValues.s3_id"
          class="mb-4"
          filled
          placeholder="Enter Key"
          v-bind="veeValidate('Key', 'Key')"
        />
        <v-text-field
          v-model="formValues.secret"
          v-validate="'required'"
          :disabled="!formValues.s3_id"
          class="mb-4"
          filled
          placeholder="Enter Secret"
          v-bind="veeValidate('Secret', 'Secret')"
        />
      </template>
    </v-card-text>
    <div class="text-center">
      <v-btn
        :disabled="!isFormValid"
        :loading="loading"
        :outlined="$vuetify.theme.dark"
        class="py-7 px-10 text-transform-none"
        color="primary"
        x-large
        @click="submitForm"
      >
        <span class="font14"> {{ isEditMode ? "Update" : "Add" }} </span>
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import {
  CREATE_STORAGE,
  GET_PROVIDERS,
  UPDATE_STORAGE,
} from "@/store/team/storage.module";
import { mapGetters, mapMutations } from "vuex";
import veeValidate from "@/mixins/veeValidate";

export default {
  mixins: [veeValidate],
  props: {
    actionsData: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      formValues: {},
      loading: false,
      storageLoading: false,
      editMode: "status",
    };
  },
  computed: {
    ...mapGetters({
      providerList: "getProvidersList",
      getTeamPlanUsage: "getTeamPlanUsage",
    }),
    isEditMode() {
      return !!(this.actionsData && this.actionsData.id);
    },
    isFormValid() {
      if (this.loading) return false;

      if (this.isEditMode) {
        if (this.editMode === "status") {
          // Status mode: check if status changed
          return this.formValues.status !== this.formValues.originalStatus;
        } else {
          // Credentials mode: both key and secret must be at least 10 chars
          return (
            this.formValues.key &&
            this.formValues.key.length >= 10 &&
            this.formValues.secret &&
            this.formValues.secret.length >= 10
          );
        }
      } else {
        // Create mode - check plan allows connecting storage
        if (!(this.getTeamPlanUsage && this.getTeamPlanUsage.connect_storage)) {
          return false;
        }
        return (
          !this.errors.any() &&
          this.formValues.s3_id &&
          this.formValues.key &&
          this.formValues.secret
        );
      }
    },
    keyError() {
      if (this.formValues.key && this.formValues.key.length > 0 && this.formValues.key.length < 10) {
        return "Key must be at least 10 characters";
      }
      return "";
    },
    secretError() {
      if (this.formValues.secret && this.formValues.secret.length > 0 && this.formValues.secret.length < 10) {
        return "Secret must be at least 10 characters";
      }
      return "";
    },
  },
  created() {
    if (this.isEditMode) {
      this.formValues = {
        ...this.actionsData,
        status: this.actionsData.status,
        originalStatus: this.actionsData.status,
        key: "",
        secret: "",
      };
    } else {
      this.formValues = this.actionsData;
    }
  },
  methods: {
    ...mapMutations({
      appendExternalStorage: "appendExternalStorage",
      updateExternalStorage: "updateExternalStorage",
    }),
    disableItem(item) {
      if (item.status !== 1) {
        return true;
      }
      return false;
    },
    async getProviderList() {
      if (!(this.providerList && this.providerList.length)) {
        this.storageLoading = true;
        await this.$store.dispatch(GET_PROVIDERS);
        this.storageLoading = false;
      }
    },
    async submitForm() {
      if (this.isEditMode) {
        await this.updateStorage();
      } else {
        await this.createStorage();
      }
    },
    async updateStorage() {
      if (this.loading) return;
      this.loading = true;

      const payload = {
        id: this.formValues.id,
      };

      if (this.editMode === "status") {
        payload.status = this.formValues.status;
      } else {
        payload.key = this.formValues.key;
        payload.secret = this.formValues.secret;
      }

      this.$store
        .dispatch(UPDATE_STORAGE, payload)
        .then((response) => {
          if (this.editMode === "credentials") {
            this.updateExternalStorage({
              id: this.formValues.id,
              key: this.formValues.key,
            });
          } else {
            this.updateExternalStorage({
              id: this.formValues.id,
              status: this.formValues.status,
            });
          }
          this.loading = false;
          this.notifyUserMessage({
            message:
              response.message ||
              (this.editMode === "status"
                ? "Storage status updated successfully"
                : "Storage credentials updated successfully"),
          });
          this.$emit("refresh");
        })
        .catch((err) => {
          this.loading = false;
          this.notifyErrorMessage(err.message);
        });
    },
    async createStorage() {
      if (await this.validateAllFields()) {
        if (this.loading) return;
        this.loading = true;
        this.$store
          .dispatch(CREATE_STORAGE, this.formValues)
          .then((response) => {
            const providerInfo = this.providerList.find(
              (provider) => provider.id === this.formValues.s3_id
            );
            const dataToAppend = {
              id: response.id,
              bucket: response.bucket || "-",
              name: providerInfo.name,
              icon: providerInfo.icon,
              endpoint: response.endpoint,
              region: providerInfo.region,
              key: this.formValues.key,
              secret: "**********",
              meta: {
                storage: 0,
                total_apps: 0,
                total_releases: 0,
              },
            };
            this.appendExternalStorage(dataToAppend);
            this.notifyUserMessage({ message: response.message });
            this.$emit("refresh", response);
            this.loading = false;
          })
          .catch((err) => {
            this.loading = false;
            this.notifyErrorMessage(err.message);
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.storage-info-header {
  background: rgba(var(--v-theme-primary), 0.05);
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
}

.theme--dark .storage-info-header {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.full-width-toggle {
  width: 100%;

  .v-btn {
    flex: 1;
  }
}
</style>
